import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Car, Plus, Bell, LogOut, Clock, CheckCircle, DollarSign, FileText, Calendar, Star, ChevronRight, Shield, MapPin, Wrench, ExternalLink, Eye, Phone, Home, ArrowLeft, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import RepairTimeline from '../components/RepairTimeline';
import SurveyModal from '../components/SurveyModal';
import { TourGuide } from '../components/TourGuide';
import { NotificationsDropdown } from '../components/NotificationsDropdown';

const CLIENT_TOUR_STEPS = [
  { selector: '[data-testid="stats-overview"]', title: 'Tu actividad de un vistazo', text: 'Solicitudes, cotizaciones recibidas y servicios completados, siempre visibles al entrar.' },
  { selector: '[data-testid="request-service-card"]', title: 'Solicita un servicio', text: 'Describe lo que tu vehículo necesita y los talleres certificados de tu zona te enviarán ofertas.' },
  { selector: '[data-testid="manage-vehicles-card"]', title: 'Tus vehículos', text: 'Cada vehículo tiene su Car Passport: un historial digital verificable, compartible y transferible.' },
  { selector: '[data-testid="dashboard-tabs"]', title: 'Todo organizado', text: 'Revisa tus solicitudes, compara cotizaciones lado a lado y da seguimiento a tus citas en tiempo real.' },
  { selector: '[data-testid="notifications-bell"]', title: 'Notificaciones en vivo', text: 'Te avisamos al instante cuando llega una nueva oferta o tu reparación avanza de etapa.' },
  { selector: null, title: '¡Listo para comenzar!', text: 'Explora Flix con libertad. Puedes repetir este tour cuando quieras con el botón de ayuda del encabezado.' },
];

const ClientDashboard = () => {
  const { user, API, sessionToken, handleLogout } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [tourActive, setTourActive] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [bidsReceived, setBidsReceived] = useState([]);
  const [talleres, setTalleres] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedAppointment, setExpandedAppointment] = useState(null);
  const [surveyModal, setSurveyModal] = useState({ open: false, appointmentId: null });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [vehiclesRes, requestsRes, notificationsRes, appointmentsRes, talleresRes] = await Promise.all([
        axios.get(`${API}/vehicles`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/service-requests`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        axios.get(`${API}/public/talleres`)
      ]);

      setVehicles(vehiclesRes.data);
      setServiceRequests(requestsRes.data);
      setNotifications(notificationsRes.data.filter(n => !n.read).slice(0, 5));
      setAppointments(appointmentsRes.data);
      
      // Crear mapa de talleres
      const talleresMap = {};
      talleresRes.data.forEach(t => {
        talleresMap[t.id] = t;
      });
      setTalleres(talleresMap);

      // Obtener bids para cada solicitud
      const allBids = [];
      for (const request of requestsRes.data) {
        try {
          const bidsRes = await axios.get(`${API}/bids?service_request_id=${request.id}`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          });
          allBids.push(...bidsRes.data.map(bid => ({ ...bid, request })));
        } catch (e) {
          // Silent fail - bid fetch errors are non-critical
        }
      }
      setBidsReceived(allBids);
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  }, [API, sessionToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoized sorted lists for performance
  const sortedServiceRequests = useMemo(() => {
    return [...serviceRequests].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [serviceRequests]);

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
  }, [appointments]);

  const sortedBidsReceived = useMemo(() => {
    return [...bidsReceived].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [bidsReceived]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'Abierta',
      'in_progress': 'En Progreso',
      'completed': 'Completada',
      'cancelled': 'Cancelada',
      'pending': 'Pendiente',
      'accepted': 'Aceptada',
      'rejected': 'Rechazada',
      'scheduled': 'Programada'
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Estadísticas
  const stats = {
    totalRequests: serviceRequests.length,
    activeRequests: serviceRequests.filter(r => r.status === 'open' || r.status === 'in_progress').length,
    completedRequests: serviceRequests.filter(r => r.status === 'completed').length,
    totalBids: bidsReceived.length,
    acceptedBids: bidsReceived.filter(b => b.status === 'accepted').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Flix</h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Botón Página Principal */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')} 
                className="text-slate-600 hover:text-purple-600"
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Inicio</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTourActive(true)}
                className="text-slate-600 hover:text-purple-600"
                data-testid="start-tour-btn"
                title="Tour guiado"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Tour</span>
              </Button>
              <NotificationsDropdown API={API} sessionToken={sessionToken} />
              <span className="text-sm font-medium text-slate-700 hidden md:inline">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-purple-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Bienvenido, {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-slate-600">Gestiona tus vehículos y solicitudes de servicio</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-overview">
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalRequests}</p>
                <p className="text-xs text-slate-500">Solicitudes</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.activeRequests}</p>
                <p className="text-xs text-slate-500">Activas</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.completedRequests}</p>
                <p className="text-xs text-slate-500">Completadas</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalBids}</p>
                <p className="text-xs text-slate-500">Cotizaciones</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card className="p-6 hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-purple-200 group" onClick={() => navigate('/client/request-service')} data-testid="request-service-card">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Plus className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Solicitar Servicio</h3>
                <p className="text-sm text-slate-600">Crea una nueva solicitud y recibe ofertas</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-purple-600 transition-colors" />
            </div>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-purple-200 group" onClick={() => navigate('/client/vehicles')} data-testid="manage-vehicles-card">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Car className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Mis Vehículos</h3>
                <p className="text-sm text-slate-600">{vehicles.length} vehículos registrados</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-green-600 transition-colors" />
            </div>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 max-w-full overflow-x-auto flex-nowrap justify-start" data-testid="dashboard-tabs">
            <TabsTrigger value="requests" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Mis Solicitudes
            </TabsTrigger>
            <TabsTrigger value="bids" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Cotizaciones Recibidas
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Citas
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Vehículos
            </TabsTrigger>
          </TabsList>

          {/* Solicitudes Tab */}
          <TabsContent value="requests" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-slate-900">Historial de Solicitudes</h3>
              <Button onClick={() => navigate('/client/request-service')} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" /> Nueva Solicitud
              </Button>
            </div>
            
            {serviceRequests.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No tienes solicitudes de servicio</p>
                <Button onClick={() => navigate('/client/request-service')} className="bg-purple-600 hover:bg-purple-700">
                  Crear Primera Solicitud
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {sortedServiceRequests.map((request) => (
                  <Card 
                    key={request.id} 
                    className="p-5 border-slate-200 hover:shadow-md hover:border-purple-300 transition-all cursor-pointer" 
                    data-testid={`service-request-${request.id}`}
                    onClick={() => navigate(`/client/request/${request.id}`)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-semibold text-lg text-slate-900">{request.service_type}</h4>
                          <Badge className={getStatusColor(request.status)}>
                            {getStatusLabel(request.status)}
                          </Badge>
                          {request.status === 'completed' && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Ver Detalle
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-600 text-sm mb-3 line-clamp-2">{request.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {formatDate(request.created_at)}
                          </span>
                          <span className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {request.bids_count} cotizaciones
                          </span>
                          {request.location?.city && (
                            <span className="text-purple-600">{request.location.city}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {request.status === 'open' && request.bids_count > 0 && (
                          <Button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/client/bids/${request.id}`);
                            }} 
                            className="bg-purple-600 hover:bg-purple-700" 
                            data-testid={`view-bids-btn-${request.id}`}
                          >
                            Ver Ofertas
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Cotizaciones Tab */}
          <TabsContent value="bids" className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Cotizaciones Recibidas</h3>
            
            {bidsReceived.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No has recibido cotizaciones aún</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {sortedBidsReceived.map((bid) => {
                  const taller = talleres[bid.taller_id];
                  const relatedVehicle = vehicles.find(v => v.id === bid.request?.vehicle_id);
                  
                  return (
                    <Card key={bid.id} className="border-slate-200 hover:shadow-lg transition-shadow overflow-hidden" data-testid={`bid-received-${bid.id}`}>
                      {/* Header Principal - Info visible sin scroll */}
                      <div className="bg-purple-600 p-5 text-white">
                        {/* Fila 1: Servicio, Estado y Precio */}
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center space-x-3">
                              <h4 className="font-bold text-2xl">{bid.request?.service_type || 'Servicio'}</h4>
                              <Badge className={`${bid.status === 'accepted' ? 'bg-green-500' : bid.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'} text-white border-0`}>
                                {getStatusLabel(bid.status)}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold">{formatPrice(bid.price)}</p>
                            <p className="text-sm text-purple-200">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {bid.estimated_time || 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Fila 2: Info del Taller, Ubicación y Fecha - VISIBLE SIN SCROLL */}
                        <div className="grid md:grid-cols-3 gap-3">
                          {/* Taller */}
                          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3">
                            <p className="text-xs text-purple-200 uppercase mb-1">Taller</p>
                            <div className="flex items-center space-x-2">
                              <Wrench className="w-5 h-5 text-white" />
                              <div>
                                <p className="font-semibold text-white truncate">
                                  {taller?.company_name || taller?.name || 'Taller'}
                                </p>
                                <div className="flex items-center space-x-1">
                                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                  <span className="text-xs text-purple-200">
                                    {taller?.rating?.toFixed(1) || '0.0'} ({taller?.total_reviews || 0})
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Ubicación */}
                          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3">
                            <p className="text-xs text-purple-200 uppercase mb-1">Ubicación</p>
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-5 h-5 text-white flex-shrink-0" />
                              <p className="text-white text-sm truncate">
                                {taller?.location?.city || taller?.location?.address || bid.request?.location?.city || 'Costa Rica'}
                              </p>
                            </div>
                          </div>

                          {/* Fecha Propuesta */}
                          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3">
                            <p className="text-xs text-purple-200 uppercase mb-1">Fecha Propuesta</p>
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-5 h-5 text-white flex-shrink-0" />
                              <p className="text-white text-sm">
                                {bid.request?.preferred_date 
                                  ? formatDate(bid.request.preferred_date)
                                  : bid.request?.preferred_dates?.[0]
                                    ? formatDate(bid.request.preferred_dates[0])
                                    : formatDate(bid.created_at)
                                }
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Fila 3: Info del vehículo */}
                        {relatedVehicle && (
                          <div className="flex items-center space-x-3 mt-3 bg-white/10 rounded-lg p-3">
                            <Car className="w-5 h-5 text-white" />
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="font-semibold">{relatedVehicle.make} {relatedVehicle.model} {relatedVehicle.year}</span>
                              <span className="text-purple-200">•</span>
                              <span className="text-purple-200">Placa: {relatedVehicle.license_plate}</span>
                              <span className="text-purple-200">•</span>
                              <span className="text-purple-200">{relatedVehicle.color}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-6">
                        <div className="grid lg:grid-cols-5 gap-6">
                          {/* Info del Taller - Columna más amplia */}
                          <div className="lg:col-span-2">
                            <h5 className="text-xs font-medium text-slate-500 uppercase mb-3 flex items-center">
                              <Wrench className="w-3 h-3 mr-1" />
                              Detalles del Taller
                            </h5>
                            {taller ? (
                              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                {/* Header del taller */}
                                <div className="flex items-start space-x-3 mb-4">
                                  <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Wrench className="w-7 h-7 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h6 className="font-bold text-slate-900 text-lg">{taller.company_name || taller.name}</h6>
                                    <div className="flex items-center mt-1">
                                      <div className="flex">
                                        {[1,2,3,4,5].map((star) => (
                                          <Star 
                                            key={star} 
                                            className={`w-4 h-4 ${star <= Math.round(taller.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} 
                                          />
                                        ))}
                                      </div>
                                      <span className="ml-2 text-sm font-medium text-slate-700">{taller.rating?.toFixed(1) || '0.0'}</span>
                                      <span className="ml-1 text-xs text-slate-500">({taller.total_reviews || 0} reseñas)</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Detalles del taller */}
                                <div className="space-y-2 mb-4">
                                  {taller.location && (
                                    <div className="flex items-start text-sm">
                                      <MapPin className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <span className="text-slate-600">
                                        {taller.location.address && `${taller.location.address}, `}
                                        {taller.location.city}
                                      </span>
                                    </div>
                                  )}
                                  {taller.phone && (
                                    <div className="flex items-center text-sm">
                                      <Phone className="w-4 h-4 text-purple-500 mr-2" />
                                      <span className="text-slate-600">{taller.phone}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Servicios ofrecidos */}
                                {taller.services_offered && taller.services_offered.length > 0 && (
                                  <div className="mb-4">
                                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">Especialidades</p>
                                    <div className="flex flex-wrap gap-1">
                                      {taller.services_offered.slice(0, 4).map((service) => (
                                        <Badge key={`service-${service}`} className="bg-purple-100 text-purple-700 text-xs">
                                          {service}
                                        </Badge>
                                      ))}
                                      {taller.services_offered.length > 4 && (
                                        <Badge className="bg-slate-100 text-slate-600 text-xs">
                                          +{taller.services_offered.length - 4} más
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Certificaciones */}
                                {taller.certifications && taller.certifications.length > 0 && (
                                  <div className="mb-4">
                                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">Certificaciones</p>
                                    <div className="flex flex-wrap gap-1">
                                      {taller.certifications.slice(0, 3).map((cert) => (
                                        <Badge key={`cert-${cert}`} className="bg-green-100 text-green-700 text-xs flex items-center">
                                          <Shield className="w-3 h-3 mr-1" />
                                          {cert}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                                  onClick={() => navigate(`/taller/${bid.taller_id}`)}
                                  data-testid={`view-taller-btn-${bid.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver Perfil Completo
                                </Button>
                              </div>
                            ) : (
                              <div className="bg-slate-50 rounded-lg p-4 text-center text-slate-500">
                                <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">Taller no disponible</p>
                              </div>
                            )}
                          </div>

                          {/* Detalle de la Cotización */}
                          <div className="lg:col-span-3">
                            <h5 className="text-xs font-medium text-slate-500 uppercase mb-3 flex items-center">
                              <FileText className="w-3 h-3 mr-1" />
                              Detalle de la Cotización
                            </h5>
                            
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                              {/* Resumen de precios */}
                              <div className="grid md:grid-cols-3 gap-4 mb-4">
                                <div className="bg-white rounded-lg p-3 border border-slate-100 text-center">
                                  <p className="text-xs text-slate-500 uppercase">Precio Total</p>
                                  <p className="text-xl font-bold text-purple-600">{formatPrice(bid.price)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-slate-100 text-center">
                                  <p className="text-xs text-slate-500 uppercase">Tiempo Estimado</p>
                                  <p className="text-xl font-bold text-slate-900 flex items-center justify-center">
                                    <Clock className="w-5 h-5 mr-1 text-purple-500" />
                                    {bid.estimated_time || 'N/A'}
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-slate-100 text-center">
                                  <p className="text-xs text-slate-500 uppercase">Válida Hasta</p>
                                  <p className="text-sm font-medium text-slate-900">
                                    {bid.valid_until ? formatDate(bid.valid_until) : '7 días'}
                                  </p>
                                </div>
                              </div>

                              {/* Descripción del servicio solicitado */}
                              <div className="mb-4">
                                <p className="text-xs font-medium text-slate-500 uppercase mb-2">Servicio Solicitado</p>
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="font-medium text-slate-900">{bid.request?.service_type}</p>
                                  {bid.request?.description && (
                                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{bid.request.description}</p>
                                  )}
                                </div>
                              </div>

                              {/* Mensaje del taller */}
                              {bid.message && (
                                <div>
                                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Mensaje del Taller</p>
                                  <div className="bg-purple-50 rounded-lg p-4 border border-slate-200">
                                    <p className="text-slate-700 italic">&ldquo;{bid.message}&rdquo;</p>
                                  </div>
                                </div>
                              )}

                              {/* Ubicaciones preferidas si existen */}
                              {bid.request?.preferred_locations && bid.request.preferred_locations.length > 0 && (
                                <div className="mt-4">
                                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Ubicación del Servicio</p>
                                  <div className="flex items-center text-sm text-slate-600">
                                    <MapPin className="w-4 h-4 mr-2 text-purple-500" />
                                    {bid.request.preferred_locations[0]?.name || bid.request.preferred_locations[0]?.address || bid.request.location?.city}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botones de acción */}
                        {bid.status === 'pending' && (
                          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-5 border-t border-slate-100 gap-4">
                            <p className="text-sm text-slate-500">
                              <Clock className="w-4 h-4 inline mr-1" />
                              Recibida hace {Math.floor((new Date() - new Date(bid.created_at)) / (1000 * 60 * 60 * 24))} días
                            </p>
                            <div className="flex space-x-3">
                              <Button 
                                variant="outline" 
                                onClick={() => navigate(`/taller/${bid.taller_id}`)}
                                className="border-purple-200 text-purple-600 hover:bg-purple-50"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Ver Taller
                              </Button>
                              <Button 
                                variant="outline" 
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`reject-bid-btn-${bid.id}`}
                              >
                                Rechazar
                              </Button>
                              <Button 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => navigate(`/client/bids/${bid.request?.id || bid.service_request_id}`)}
                                data-testid={`accept-bid-btn-${bid.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Aceptar Oferta
                              </Button>
                            </div>
                          </div>
                        )}

                        {bid.status === 'accepted' && (
                          <div className="flex justify-between items-center mt-6 pt-5 border-t border-slate-100">
                            <div className="flex items-center text-green-600">
                              <CheckCircle className="w-5 h-5 mr-2" />
                              <span className="font-medium">Oferta aceptada - Cita programada</span>
                            </div>
                            <Button 
                              onClick={() => navigate(`/client/request/${bid.request?.id || bid.service_request_id}`)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              Ver Detalle Completo
                            </Button>
                          </div>
                        )}

                        {bid.status === 'rejected' && (
                          <div className="flex justify-end mt-6 pt-5 border-t border-slate-100">
                            <Badge className="bg-slate-100 text-slate-600 px-4 py-2">
                              Oferta No Seleccionada
                            </Badge>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Citas Tab */}
          <TabsContent value="appointments" className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Mis Citas</h3>
            
            {appointments.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes citas programadas</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {sortedAppointments.map((appointment) => {
                  const taller = talleres[appointment.taller_id];
                  const vehicle = vehicles.find(v => v.id === appointment.vehicle_id);
                  const relatedRequest = serviceRequests.find(r => r.id === appointment.service_request_id);
                  
                  return (
                    <Card key={appointment.id} className="border-slate-200 hover:shadow-lg transition-shadow overflow-hidden" data-testid={`appointment-${appointment.id}`}>
                      {/* Header con fecha y estado */}
                      <div className={`p-4 border-b ${
                        appointment.status === 'completed' ? 'bg-gradient-to-r from-green-50 to-white border-green-100' :
                        appointment.status === 'scheduled' ? 'bg-white border-slate-200' :
                        appointment.status === 'in_progress' ? 'bg-gradient-to-r from-yellow-50 to-white border-yellow-100' :
                        'bg-gradient-to-r from-gray-50 to-white border-slate-100'
                      }`}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              appointment.status === 'completed' ? 'bg-green-100' :
                              appointment.status === 'scheduled' ? 'bg-purple-100' :
                              appointment.status === 'in_progress' ? 'bg-yellow-100' :
                              'bg-slate-100'
                            }`}>
                              <Calendar className={`w-6 h-6 ${
                                appointment.status === 'completed' ? 'text-green-600' :
                                appointment.status === 'scheduled' ? 'text-purple-600' :
                                appointment.status === 'in_progress' ? 'text-yellow-600' :
                                'text-slate-600'
                              }`} />
                            </div>
                            <div>
                              <h4 className="font-semibold text-lg text-slate-900">
                                {formatDate(appointment.scheduled_date)}
                              </h4>
                              <p className="text-sm text-slate-500">
                                {relatedRequest?.service_type || 'Servicio programado'}
                              </p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(appointment.status)}>
                            {getStatusLabel(appointment.status)}
                          </Badge>
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="grid md:grid-cols-3 gap-6">
                          {/* Info del Taller */}
                          <div className="md:col-span-1">
                            <h5 className="text-xs font-medium text-slate-500 uppercase mb-3 flex items-center">
                              <Wrench className="w-3 h-3 mr-1" />
                              Taller Asignado
                            </h5>
                            {taller ? (
                              <div className="bg-slate-50 rounded-lg p-4">
                                <div className="flex items-start space-x-3">
                                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Wrench className="w-6 h-6 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h6 className="font-semibold text-slate-900 truncate">{taller.company_name || taller.name}</h6>
                                    <div className="flex items-center mt-1">
                                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                      <span className="ml-1 text-sm font-medium text-slate-700">{taller.rating?.toFixed(1) || '0.0'}</span>
                                      <span className="ml-1 text-xs text-slate-500">({taller.total_reviews || 0} reseñas)</span>
                                    </div>
                                    {taller.location?.city && (
                                      <div className="flex items-center mt-2 text-xs text-slate-500">
                                        <MapPin className="w-3 h-3 mr-1" />
                                        {taller.location.city}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full mt-3 border-purple-200 text-purple-600 hover:bg-purple-50"
                                  onClick={() => navigate(`/taller/${appointment.taller_id}`)}
                                  data-testid={`view-taller-btn-${appointment.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver Perfil del Taller
                                </Button>
                              </div>
                            ) : (
                              <div className="bg-slate-50 rounded-lg p-4 text-center text-slate-500">
                                <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">Taller no disponible</p>
                              </div>
                            )}
                          </div>

                          {/* Info del Vehículo y Detalles */}
                          <div className="md:col-span-2">
                            <h5 className="text-xs font-medium text-slate-500 uppercase mb-3 flex items-center">
                              <FileText className="w-3 h-3 mr-1" />
                              Detalle de la Cita
                            </h5>
                            
                            <div className="space-y-3">
                              {vehicle && (
                                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                                  <span className="text-slate-600 flex items-center">
                                    <Car className="w-4 h-4 mr-2 text-purple-500" />
                                    Vehículo
                                  </span>
                                  <span className="font-medium text-slate-900">
                                    {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                                <span className="text-slate-600 flex items-center">
                                  <FileText className="w-4 h-4 mr-2 text-purple-500" />
                                  Servicio
                                </span>
                                <span className="font-medium text-slate-900">
                                  {relatedRequest?.service_type || 'No especificado'}
                                </span>
                              </div>

                              {relatedRequest?.description && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Descripción</p>
                                  <div className="bg-purple-50 rounded-lg p-3 border border-slate-200">
                                    <p className="text-sm text-slate-700">{relatedRequest.description}</p>
                                  </div>
                                </div>
                              )}

                              {appointment.status === 'completed' && (
                                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                                  <div className="flex items-center text-green-700">
                                    <Shield className="w-5 h-5 mr-2" />
                                    <span className="font-medium">Registrado en Car Passport</span>
                                  </div>
                                  <p className="text-sm text-green-600 mt-1">
                                    Este servicio está verificado y forma parte del historial de tu vehículo.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botones de acción */}
                        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-100">
                          <Button 
                            variant="outline" 
                            onClick={() => navigate(`/taller/${appointment.taller_id}`)}
                            className="border-purple-200 text-purple-600 hover:bg-purple-50"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Ver Taller
                          </Button>
                          
                          {appointment.status === 'scheduled' && (
                            <>
                              <Button 
                                variant="outline" 
                                className="border-red-200 text-red-600 hover:bg-red-50"
                              >
                                Cancelar Cita
                              </Button>
                              <Button className="bg-purple-600 hover:bg-purple-700">
                                <Calendar className="w-4 h-4 mr-2" />
                                Reprogramar
                              </Button>
                            </>
                          )}
                          
                          {appointment.status === 'completed' && vehicle && (
                            <Button 
                              onClick={() => navigate(`/client/car-passport/${vehicle.id}`)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Ver Car Passport
                            </Button>
                          )}

                          {appointment.status === 'completed' && (
                            <Button
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => setSurveyModal({ open: true, appointmentId: appointment.id })}
                              data-testid={`open-survey-btn-${appointment.id}`}
                            >
                              <Star className="w-4 h-4 mr-2" />
                              Calificar experiencia
                            </Button>
                          )}

                          <Button
                            variant="outline"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() => setExpandedAppointment(expandedAppointment === appointment.id ? null : appointment.id)}
                            data-testid={`toggle-repair-progress-${appointment.id}`}
                          >
                            <Wrench className="w-4 h-4 mr-2" />
                            {expandedAppointment === appointment.id ? 'Ocultar progreso' : 'Ver progreso de reparación'}
                          </Button>
                        </div>

                        {expandedAppointment === appointment.id && (
                          <div className="mt-6 pt-6 border-t border-slate-200">
                            <RepairTimeline appointmentId={appointment.id} readOnly={true} />
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Vehículos Tab */}
          <TabsContent value="vehicles" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-slate-900">Mis Vehículos</h3>
              <Button onClick={() => navigate('/client/vehicles')} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" /> Agregar Vehículo
              </Button>
            </div>
            
            {vehicles.length === 0 ? (
              <Card className="p-8 text-center border-slate-200">
                <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No tienes vehículos registrados</p>
                <Button onClick={() => navigate('/client/vehicles')} className="bg-purple-600 hover:bg-purple-700">
                  Agregar Vehículo
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicles.map((vehicle) => (
                  <Card key={vehicle.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow" data-testid={`vehicle-card-${vehicle.id}`}>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                        <Car className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{vehicle.make} {vehicle.model}</h4>
                        <p className="text-sm text-slate-600">{vehicle.year} • {vehicle.color}</p>
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-slate-500">Placa: <span className="font-medium">{vehicle.license_plate}</span></p>
                          <p className="text-xs text-slate-500">Kilometraje: <span className="font-medium">{vehicle.mileage?.toLocaleString()} km</span></p>
                        </div>
                        <div className="mt-3 flex items-center text-xs text-purple-600">
                          <Shield className="w-3 h-3 mr-1" />
                          Car Passport activo
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <SurveyModal
        open={surveyModal.open}
        onOpenChange={(o) => setSurveyModal({ open: o, appointmentId: o ? surveyModal.appointmentId : null })}
        appointmentId={surveyModal.appointmentId}
      />

      {/* Botón flotante para volver atrás */}
      <Button
        onClick={() => navigate(-1)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-purple-600 hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all z-50"
        data-testid="back-btn-floating"
      >
        <ArrowLeft className="w-6 h-6 text-white" />
      </Button>

      <TourGuide steps={CLIENT_TOUR_STEPS} active={tourActive} onClose={() => setTourActive(false)} />
    </div>
  );
};

export default ClientDashboard;
